/**
 * documentViewerGeometry.ts
 *
 * Pure helpers for DocumentViewer: bounding-box normalisation, highlight
 * lookup per page, zoom and fit-to-width maths. Kept free of React and pdfjs so
 * they are cheap to unit test.
 */

/**
 * Field geometry as stored on extracted fields: fractions (0..1) of the page,
 * measured from the top-left corner. Same shape FileReview reads from
 * `bounding_box` / `boundingBox`.
 */
export interface BoundingBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** One clickable region on a page. `id` is the owner's key (e.g. field id). */
export interface ViewerHighlight {
  id: string;
  /** 1-based absolute PDF page. */
  page: number;
  boundingBox: BoundingBox;
  /** Short badge drawn on the box, for example the field's number in the list. */
  badge?: string;
}

export const ZOOM_LEVELS: readonly number[] = [0.75, 1, 1.25, 1.5, 2];
export const DEFAULT_ZOOM_INDEX = 1;
/** Horizontal breathing room (px) kept around the page when fitting to width. */
export const PAGE_GUTTER_PX = 32;
/** Viewer never drops below this width (ui-design-system, Layout limits). */
export const VIEWER_MIN_WIDTH_PX = 320;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/**
 * Accepts the shapes found in extraction data (`boundingBox`, `bounding_box`,
 * or a bare object) and returns a clamped BoundingBox, or null when the data is
 * missing or unusable.
 */
export function normalizeBoundingBox(raw: unknown): BoundingBox | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const src = (r.boundingBox ?? r.bounding_box ?? r) as Record<string, unknown>;
  if (!src || typeof src !== 'object') return null;
  const top = Number(src.top);
  const left = Number(src.left);
  const width = Number(src.width);
  const height = Number(src.height);
  if (![top, left, width, height].every(Number.isFinite)) return null;
  if (width <= 0 || height <= 0) return null;
  const l = clamp01(left);
  const t = clamp01(top);
  return {
    top: t,
    left: l,
    width: Math.min(clamp01(width), 1 - l),
    height: Math.min(clamp01(height), 1 - t),
  };
}

/** Percent-based absolute-position style so overlays track any page size. */
export function highlightBoxStyle(box: BoundingBox): {
  top: string;
  left: string;
  width: string;
  height: string;
} {
  const pct = (n: number) => `${(clamp01(n) * 100).toFixed(3)}%`;
  return {
    top: pct(box.top),
    left: pct(box.left),
    width: pct(box.width),
    height: pct(box.height),
  };
}

export function highlightsOnPage(
  highlights: ReadonlyArray<ViewerHighlight>,
  page: number
): ViewerHighlight[] {
  return highlights.filter((h) => h.page === page);
}

/**
 * Whether a field says where it was read. An empty field with no bounding box
 * was not found on any page: its `page` is only the parser's default (1), so
 * selecting it must not move the viewer.
 */
export function hasPageLocation(field: {
  value: string;
  boundingBox: BoundingBox | null;
}): boolean {
  return field.boundingBox !== null || field.value.trim() !== '';
}

/** Page of the highlight with this id, or null if unknown / no id. */
export function findHighlightPage(
  highlights: ReadonlyArray<ViewerHighlight>,
  id: string | null | undefined
): number | null {
  if (id == null) return null;
  return highlights.find((h) => h.id === id)?.page ?? null;
}

/**
 * Returns `items` in reading order: by page, then top to bottom, then left to
 * right. Boxes whose tops fall within half a box height of the first box on a
 * line count as the same line, so two values on one line sort by `left` instead
 * of by a few pixels of vertical jitter. Items without a box keep their
 * original order after the located items of their page. The input is not
 * modified and the sort is stable.
 */
export function sortInReadingOrder<T>(
  items: ReadonlyArray<T>,
  locate: (item: T) => { page: number; boundingBox: BoundingBox | null }
): T[] {
  const byPage = new Map<number, Array<{ item: T; box: BoundingBox | null; index: number }>>();
  items.forEach((item, index) => {
    const { page, boundingBox } = locate(item);
    const list = byPage.get(page) ?? [];
    list.push({ item, box: boundingBox, index });
    byPage.set(page, list);
  });

  const out: T[] = [];
  for (const page of Array.from(byPage.keys()).sort((a, b) => a - b)) {
    const entries = byPage.get(page)!;
    const located = entries
      .filter((e) => e.box)
      .sort((a, b) => a.box!.top - b.box!.top || a.index - b.index);
    const unlocated = entries.filter((e) => !e.box);

    let line: typeof located = [];
    let lineTop = 0;
    let lineTolerance = 0;
    const flush = () => {
      line.sort((a, b) => a.box!.left - b.box!.left || a.index - b.index);
      out.push(...line.map((e) => e.item));
      line = [];
    };
    for (const entry of located) {
      if (line.length > 0 && entry.box!.top - lineTop > lineTolerance) flush();
      if (line.length === 0) {
        lineTop = entry.box!.top;
        lineTolerance = entry.box!.height / 2;
      }
      line.push(entry);
    }
    flush();
    out.push(...unlocated.map((e) => e.item));
  }
  return out;
}

export function clampPage(page: number, totalPages: number): number {
  if (!Number.isFinite(page) || totalPages < 1) return 1;
  return Math.min(Math.max(1, Math.round(page)), totalPages);
}

/**
 * CSS scale that fits a page of `pageWidth` (at scale 1) into `containerWidth`,
 * multiplied by the user's zoom. Falls back to the zoom alone before the
 * container has been measured.
 */
export function computeCssScale(
  containerWidth: number,
  pageWidth: number,
  zoom: number
): number {
  if (!(pageWidth > 0) || !(containerWidth > 0)) return zoom;
  const fit = Math.max(0.1, (containerWidth - PAGE_GUTTER_PX) / pageWidth);
  return fit * zoom;
}
